
import { Shield, AlertCircle, Database } from "lucide-react";
import { useEffect } from "react";

interface ResultCardProps {
  icon: React.ElementType;
  percentage: string;
  title: string;
  description: string;
}

const ResultCard = ({ icon: Icon, percentage, title, description }: ResultCardProps) => (
  <article className="bg-white rounded-xl shadow-sm p-10 border border-gray-100 text-center hover:shadow-md transition-all duration-300 hover:border-edgefi-purple/20 hover:-translate-y-1">
    <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-edgefi-purple/10 flex items-center justify-center">
      <Icon className="h-8 w-8 text-edgefi-purple" aria-hidden="true" />
    </div>
    <h3 className="text-4xl font-bold text-edgefi-purple mb-3">{percentage}</h3>
    <p className="text-xl font-semibold text-gray-900 mb-4">{title}</p>
    <p className="text-gray-600">
      {description}
    </p>
  </article>
);

export const ResultsSection = () => {
  const results = [
    {
      icon: Shield,
      percentage: "85%",
      title: "Faster Threat Detection",
      description: "You'll identify potential security threats 85% faster, giving you the peace of mind that comes with early detection and minimal damage."
    },
    {
      icon: AlertCircle,
      percentage: "90%",
      title: "Reduction in Vulnerabilities",
      description: "Within six months, you'll experience a 90% reduction in critical vulnerabilities, dramatically reducing your risk exposure."
    },
    {
      icon: Database,
      percentage: "60%",
      title: "Lower Security Costs",
      description: "You'll achieve significant savings on security-related costs through prevention rather than expensive emergency remediation."
    }
  ];

  // Add JSON-LD structured data to the page
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Managed Cybersecurity Services",
      "provider": {
        "@type": "Organization",
        "name": "edgefi"
      },
      "serviceType": "Cybersecurity",
      "offers": {
        "@type": "Offer",
        "description": "Comprehensive protection for your digital assets"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section 
      className="py-24 bg-gradient-to-b from-edgefi-white to-white"
      aria-labelledby="results-heading"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <span className="inline-block px-4 py-1.5 bg-edgefi-purple/10 text-edgefi-purple rounded-full text-sm font-medium mb-6">Your Success Story</span>
          <h2 id="results-heading" className="text-3xl sm:text-4xl font-bold mb-8 text-gray-900">
            The Security Transformation You'll Experience
          </h2>
          <p className="text-xl text-gray-600">
            These aren't just numbers—they represent the real security improvements you'll achieve and the peace of mind you'll gain.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-6xl mx-auto mb-12">
          {results.map((result, index) => (
            <ResultCard 
              key={index}
              icon={result.icon}
              percentage={result.percentage}
              title={result.title}
              description={result.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
